import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import moment from "moment";
import 'react-datepicker/dist/react-datepicker.css'

import IntervalBar from "components/IntervalBar/BadgeIntervalBar";
import CSVExporter from "components/Table/CSVExporter";
import NTMTable from "../../components/Table/NTMTable";
import Image from "../../components/Image";
import { alertIcon, cardIcon } from 'img/icons';
import style from "../Dashboard/style.module.scss";
import { get, BADGE, getErrorMessage } from "../../service/Client";


export default function CardAccessPage() {
	const project = useSelector(store => store.session.project)
	const cards = useSelector(store => store.badge.cards)
	const { card } = useParams()
	const queryClient = useQueryClient()
	const { enqueueSnackbar } = useSnackbar()

	const [interval, setInterval] = useState('daily') // 'daily' || 'custom'
	const [startDate, setStartDate] = useState()
	const [endDate, setEndDate] = useState()

	function showError(e) {
		enqueueSnackbar(getErrorMessage(e), { variant: "error" });
	}

	let { data: accesses } = useQuery(['accesses', { pid: project?.id, cid: card }, { interval, startDate, endDate }], () => {
		if (interval === 'daily') {
			return get(`${BADGE}/project/${project?.id}/cards/${card}/accesses/today`)
		} else if (startDate !== null && endDate !== null) { // custom
			return get(`${BADGE}/project/${project?.id}/cards/${card}/accesses/custom`, { params: { start: startDate.format("YYYY-MM-DD"), end: endDate.format("YYYY-MM-DD") } })
		}
	}, {
		enabled: !!project && (interval !== 'custom' || (!!startDate && !!endDate)),
		initialData: [],
		onError: showError,
		refetchInterval: (interval === 'daily') ? 10000 : false,
		select: data => data.sort((a, b) => a.checkin - b.checkin)
	})

	useEffect(() => { queryClient.invalidateQueries('accesses') }, [interval, startDate, endDate, queryClient])

	let getDuration = useCallback((checkin, checkout) => {
		if (!checkout) {
			let expired = moment().diff(moment(checkin), "minutes") > (60 * 10);
			if (expired) return <Image src={alertIcon} />;
			checkout = new Date();
		}
		let duration = moment.duration(moment(checkout).diff(moment(checkin)));
		return <div>{moment(`${duration.hours()}:${duration.minutes()}:${duration.seconds()}`, "H:m:s").format("HH:mm:ss")}</div>
	}, [])

	let dateElement = useCallback((date, alert) => (
		<div>{date ? new Date(date).toLocaleString('it-IT') : alert ? <Image src={alertIcon} /> : "-"}</div>
	), [])

	let toCSVdata = useCallback(data => data.map(d => {
		let duration = moment.duration(moment(d.checkout).diff(moment(d.checkin)))
		return {
			"Card id": d.card,
			"Label": d.cardLabel,
			"Id edificio": d.building,
			"Nome edificio": d.buildingName,
			"Check in": moment(d.checkin).tz("Europe/Rome").format("DD/MM/YYYY HH:mm"),
			"Check out": !!d.checkout ? moment(d.checkout).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : null,
			"Tempo trascorso": !!d.checkout ? moment(`${duration.hours()}:${duration.minutes()}:${duration.seconds()}`, "H:m:s").format("HH:mm:ss") : null,
		}
	}), [])

	let cardObj = cards.find(c => c.id === card)
	let title = cardObj?.name || card

	let header = [
		{
			Cell: () => <Image src={cardIcon} />,
			sortable: false
		},
		{
			Header: "nome edificio",
			filterable: true,
			accessor: 'buildingName',
			Cell: props => props.original.building ? <p>{props.original.buildingName}</p> : <Image src={alertIcon} />
		},
		{
			Header: "Check-In",
			accessor: 'checkin',
			Cell: props => dateElement(props.original.checkin)
		},
		{
			Header: "Check-Out",
			accessor: 'checkout',
			Cell: props => dateElement(props.original.checkout, moment().diff(moment(props.original.checkin), "minutes") > (60 * 10))
		},
		{
			Header: "TEMPO trascorso",
			Cell: props => getDuration(props.original.checkin, props.original.checkout),
			sortable: false
		},
		{
			Cell: props => props.original.building && (
				<Link
					to={{ pathname: `/buildingaccess/${props.original.building}` }}
					className={style.link}
				>
					{"Visualizza accessi"}
				</Link>
			)
		}
	]

	return (
		<div className="animated fadeIn">
			<IntervalBar
				interval={interval}
				setInterval={setInterval}
				startDate={startDate}
				setStartDate={setStartDate}
				endDate={endDate}
				setEndDate={setEndDate}
			/>
			<div style={{ height: "1rem" }} />
			<CSVExporter title={"Scarica"} name={"Badge"} data={toCSVdata(accesses)} />
			<NTMTable
				title={title}
				header={header}
				body={accesses.reverse()}
				defaultPageSize={10}
			/>
		</div>
	);
}
